import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { normalizeEnglishDigitsInput, normalizePhoneInput } from '../utils/validation';
import './ForgotPasswordModal.css';

export default function ForgotPasswordModal({ isOpen, onClose, portal = 'gp', tenantSlug = '', onResetSuccess }) {
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP, 3: Reset, 4: Success
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [timerSeconds, setTimerSeconds] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setPhone('');
      setOtp('');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setInfoMsg('');
      setLoading(false);
      setTimerSeconds(0);
    }
  }, [isOpen]);

  useEffect(() => {
    let interval = null;
    if (timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerSeconds]);

  if (!isOpen) return null;

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMsg('');
    const cleanPhone = normalizePhoneInput(phone);
    if (cleanPhone.length !== 10) {
      return setError('कृपया १० अंकी वैध मोबाईल नंबर प्रविष्ट करा.');
    }

    setLoading(true);
    try {
      const res = await authAPI.requestForgotPasswordOtp({
        phone: cleanPhone,
        portal,
        tenant_slug: portal === 'gp' ? tenantSlug : undefined,
      });

      setInfoMsg(res.data.message || 'WhatsApp वर ६ अंकी OTP पाठवला आहे.');
      setStep(2);
      setTimerSeconds(300); // 5 mins
    } catch (err) {
      if (err.response?.data?.error === 'WhatsApp OTP is not configured yet') {
        setError('WhatsApp OTP सेवा सध्या उपलब्ध नाही. कृपया ग्रामपंचायत कार्यालयाशी संपर्क साधून मदत घ्या.');
      } else {
        setError(err.response?.data?.error || 'OTP पाठवताना त्रुटी आली. कृपया नोंदणीकृत मोबाईल नंबर तपासा.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMsg('');
    const cleanOtp = normalizeEnglishDigitsInput(otp, 6);
    if (cleanOtp.length !== 6) {
      return setError('कृपया ६ अंकी OTP प्रविष्ट करा.');
    }

    setLoading(true);
    try {
      const res = await authAPI.verifyForgotPasswordOtp({
        phone: normalizePhoneInput(phone),
        otp: cleanOtp,
        portal,
        tenant_slug: portal === 'gp' ? tenantSlug : undefined,
      });

      setResetToken(res.data.reset_token);
      setInfoMsg('OTP पडताळणी यशस्वी! कृपया नवीन पासवर्ड तयार करा.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'OTP चुकीचा आहे किंवा मुदत संपली आहे.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMsg('');

    if (newPassword.length < 6) {
      return setError('नवीन पासवर्ड किमान ६ अक्षरांचा असावा.');
    }
    if (newPassword !== confirmPassword) {
      return setError('नवीन पासवर्ड आणि पुष्टी पासवर्ड जुळत नाहीत.');
    }

    setLoading(true);
    try {
      await authAPI.resetPasswordWithToken({
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'पासवर्ड रीसेट करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimer = (sec) => {
    const mins = Math.floor(sec / 60);
    const remainder = sec % 60;
    return `${mins}:${remainder < 10 ? '0' : ''}${remainder}`;
  };

  return (
    <div className="fgp-overlay" onClick={onClose}>
      <div className="fgp-modal" onClick={e => e.stopPropagation()}>
        <header className="fgp-header">
          <div className="fgp-header-icon">🔑</div>
          <div>
            <h3>पासवर्ड विसरलात? (Forgot Password)</h3>
            <p>OTP पडताळणीद्वारे तुमचा पासवर्ड रीसेट करा</p>
          </div>
          <button type="button" className="fgp-close-btn" onClick={onClose}>✕</button>
        </header>

        {/* Step Indicator */}
        <div className="fgp-steps">
          <div className={`fgp-step-item ${step >= 1 ? 'active' : ''}`}>1. मोबाईल नंबर</div>
          <div className={`fgp-step-item ${step >= 2 ? 'active' : ''}`}>2. OTP पडताळणी</div>
          <div className={`fgp-step-item ${step >= 3 ? 'active' : ''}`}>3. नवीन पासवर्ड</div>
        </div>

        {error && <div className="fgp-alert fgp-alert-error">⚠️ {error}</div>}
        {infoMsg && <div className="fgp-alert fgp-alert-info">ℹ️ {infoMsg}</div>}

        {/* STEP 1: Phone */}
        {step === 1 && (
          <form onSubmit={handleRequestOtp} className="fgp-form">
            <div className="fgp-field">
              <label>नोंदणीकृत १० अंकी मोबाईल नंबर <span className="req">*</span></label>
              <div className="fgp-input-wrap">
                <span className="fgp-input-icon">📱</span>
                <input
                  type="tel"
                  className="fgp-english-input"
                  lang="en"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="उदा. 9876543210"
                  value={phone}
                  onChange={e => setPhone(normalizePhoneInput(e.target.value))}
                  autoComplete="tel"
                  required
                  autoFocus
                />
              </div>
              <small className="fgp-hint">खात्याशी जोडलेल्या मोबाईल नंबरवर OTP पाठवला जाईल.</small>
            </div>

            <div className="fgp-actions">
              <button type="button" className="fgp-btn-sec" onClick={onClose}>रद्द करा</button>
              <button type="submit" className="fgp-btn-pri" disabled={loading || phone.length !== 10}>
                {loading ? 'OTP पाठवत आहे…' : 'OTP पाठवा 📲'}
              </button>
            </div>
          </form>
        )}

        {/* STEP 2: OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="fgp-form">
            <div className="fgp-field">
              <label>६ अंकी OTP प्रविष्ट करा <span className="req">*</span></label>
              <div className="fgp-input-wrap">
                <span className="fgp-input-icon">🛡️</span>
                <input
                  type="text"
                  className="fgp-english-input"
                  lang="en"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="••••••"
                  value={otp}
                  onChange={e => setOtp(normalizeEnglishDigitsInput(e.target.value, 6))}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </div>
              <div className="fgp-timer-row">
                {timerSeconds > 0 ? (
                  <span>OTP वैध वेळ: <strong>{formatTimer(timerSeconds)}</strong></span>
                ) : (
                  <button type="button" className="fgp-link-btn" onClick={handleRequestOtp}>
                    पुन्हा OTP पाठवा
                  </button>
                )}
                <button type="button" className="fgp-link-btn" onClick={() => setStep(1)}>
                  नंबर बदला
                </button>
              </div>
            </div>

            <div className="fgp-actions">
              <button type="button" className="fgp-btn-sec" onClick={onClose}>रद्द करा</button>
              <button type="submit" className="fgp-btn-pri" disabled={loading || otp.length !== 6}>
                {loading ? 'पडताळणी होत आहे…' : 'OTP पडताळणी करा ✓'}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: Reset Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="fgp-form">
            <div className="fgp-field">
              <label>नवीन पासवर्ड <span className="req">*</span></label>
              <div className="fgp-input-wrap">
                <span className="fgp-input-icon">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="नवीन पासवर्ड टाका (किमान ६ अक्षरे)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="fgp-pwd-toggle"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? '👁️' : '🙈'}
                </button>
              </div>
            </div>

            <div className="fgp-field">
              <label>नवीन पासवर्ड पुष्टी <span className="req">*</span></label>
              <div className="fgp-input-wrap">
                <span className="fgp-input-icon">🔐</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="नवीन पासवर्ड पुन्हा प्रविष्ट करा"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="fgp-actions">
              <button type="button" className="fgp-btn-sec" onClick={onClose}>रद्द करा</button>
              <button type="submit" className="fgp-btn-pri" disabled={loading || !newPassword || !confirmPassword}>
                {loading ? 'अपडेट होत आहे…' : 'पासवर्ड रीसेट करा 💾'}
              </button>
            </div>
          </form>
        )}

        {/* STEP 4: Success */}
        {step === 4 && (
          <div className="fgp-success-box">
            <div className="fgp-success-icon">🎉</div>
            <h4>पासवर्ड यशस्वीरीत्या बदलला!</h4>
            <p>तुमचा नवीन पासवर्ड सेट झाला आहे. आता तुम्ही नवीन पासवर्ड वापरून लॉग इन करू शकता.</p>
            <button
              type="button"
              className="fgp-btn-pri full-width"
              onClick={() => {
                onClose();
                if (onResetSuccess) onResetSuccess(phone);
              }}
            >
              लॉग इन करा →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
